// src/pages/admin/Users.jsx
// git commit: "redesign(Users): full admin user management with detail panel, wallet adjust, stats"
import { useEffect, useState, useCallback } from "react";
import adminApi from "../../api/adminApi";
import { toast } from "react-toastify";
import Currency from "../../components/Currency";
import {
  Search,
  RefreshCw,
  Shield,
  ShieldOff,
  Trash2,
  ChevronRight,
  X,
  Wallet,
  Users as UsersIcon,
  UserCheck,
  UserX,
  Mail,
  Calendar,
  TrendingUp,
  Plus,
  Minus,
  Eye,
  EyeOff,
} from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustType, setAdjustType] = useState("add");
  const [adjusting, setAdjusting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.get("/users");
      setUsers(res.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Update selected user when users list refreshes
  useEffect(() => {
    if (selectedUser) {
      const updated = users.find((u) => u._id === selectedUser._id);
      if (updated) setSelectedUser(updated);
    }
  }, [users]);

  // Stats
  const stats = {
    total: users.length,
    active: users.filter((u) => !u.isBlocked).length,
    blocked: users.filter((u) => u.isBlocked).length,
    totalBalance: users.reduce((sum, u) => sum + (u.wallet?.balance || 0), 0),
  };

  // Filtered list
  const filtered = users.filter((u) => {
    const matchSearch =
      !search.trim() ||
      u.username?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && !u.isBlocked) ||
      (statusFilter === "blocked" && u.isBlocked);
    return matchSearch && matchStatus;
  });

  const handleBlock = async (user) => {
    const action = user.isBlocked ? "unblock" : "block";
    setActionLoading(`block-${user._id}`);
    try {
      await adminApi.patch(`/users/${user._id}/${action}`);
      toast.success(`User ${action}ed successfully`);
      fetchUsers();
    } catch {
      toast.error("Operation failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (user) => {
    if (
      !window.confirm(
        `Permanently delete ${user.username}? This cannot be undone.`
      )
    )
      return;
    setActionLoading(`delete-${user._id}`);
    try {
      await adminApi.delete(`/users/${user._id}`);
      toast.success("User deleted");
      if (selectedUser?._id === user._id) setSelectedUser(null);
      fetchUsers();
    } catch {
      toast.error("Delete failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleWalletAdjust = async () => {
    const amount = Number(adjustAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (
      !window.confirm(
        `${adjustType === "add" ? "Add" : "Deduct"} ₦${amount.toLocaleString()} ${
          adjustType === "add" ? "to" : "from"
        } ${selectedUser.username}'s wallet?`
      )
    )
      return;

    setAdjusting(true);
    try {
      await adminApi.post("/wallets/topup", {
        userId: selectedUser._id,
        amount: amount,
        type: adjustType,
      });
      toast.success(
        `₦${amount.toLocaleString()} ${
          adjustType === "add" ? "added to" : "deducted from"
        } wallet`
      );
      setAdjustAmount("");
      fetchUsers();
    } catch {
      toast.error("Wallet adjustment failed");
    } finally {
      setAdjusting(false);
    }
  };

  return (
    <div className="space-y-5 h-full">
      {/* ── Stat Cards ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            label: "Total Users",
            value: stats.total,
            icon: UsersIcon,
            color: "text-blue-600 dark:text-blue-400",
            bg: "bg-blue-50 dark:bg-blue-900/20",
          },
          {
            label: "Active",
            value: stats.active,
            icon: UserCheck,
            color: "text-green-600 dark:text-green-400",
            bg: "bg-green-50 dark:bg-green-900/20",
          },
          {
            label: "Blocked",
            value: stats.blocked,
            icon: UserX,
            color: "text-red-600 dark:text-red-400",
            bg: "bg-red-50 dark:bg-red-900/20",
          },
          {
            label: "Total Balance",
            value: null,
            currency: stats.totalBalance,
            icon: Wallet,
            color: "text-purple-600 dark:text-purple-400",
            bg: "bg-purple-50 dark:bg-purple-900/20",
          },
        ].map(({ label, value, currency, icon: Icon, color, bg }) => (
          <div
            key={label}
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4"
          >
            <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-3`}>
              <Icon size={16} className={color} />
            </div>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {currency !== undefined ? (
                <Currency amount={currency} />
              ) : (
                value.toLocaleString()
              )}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Main panel ───────────────────────────────────────── */}
      <div className="flex gap-5 min-h-0">
        {/* Left: Users list */}
        <div
          className={`flex flex-col bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-300 ${
            selectedUser ? "w-full lg:w-3/5" : "w-full"
          }`}
        >
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-100 dark:border-gray-800">
            <div className="relative flex-1">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search username or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="blocked">Blocked</option>
              </select>
              <button
                onClick={fetchUsers}
                disabled={loading}
                className="p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-400 transition"
              >
                <RefreshCw
                  size={15}
                  className={`text-gray-500 ${loading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>

          {/* Count row */}
          <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {filtered.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {users.length}
              </span>{" "}
              users
            </p>
          </div>

          {/* Table — desktop */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw
                  size={24}
                  className="text-purple-500 animate-spin"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Loading users...
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="text-center">
                <UsersIcon
                  size={36}
                  className="mx-auto text-gray-300 dark:text-gray-600 mb-2"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No users found
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto flex-1">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                    <tr>
                      {["User", "Balance", "Spent", "Status", "Joined", ""].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-5 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                    {filtered.map((user) => (
                      <tr
                        key={user._id}
                        onClick={() =>
                          setSelectedUser(
                            selectedUser?._id === user._id ? null : user
                          )
                        }
                        className={`cursor-pointer transition-all ${
                          selectedUser?._id === user._id
                            ? "bg-purple-50 dark:bg-purple-900/10"
                            : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                        }`}
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-xs font-bold flex-shrink-0">
                              {user.username?.[0]?.toUpperCase() || "U"}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.username}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm font-semibold text-gray-900 dark:text-white">
                          <Currency amount={user.wallet?.balance || 0} />
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400">
                          <Currency amount={user.wallet?.totalSpent || 0} />
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.isBlocked
                                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                user.isBlocked ? "bg-red-500" : "bg-green-500"
                              }`}
                            />
                            {user.isBlocked ? "Blocked" : "Active"}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-400 dark:text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString("en-NG", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-5 py-3.5">
                          <ChevronRight
                            size={16}
                            className={`text-gray-300 transition-transform ${
                              selectedUser?._id === user._id
                                ? "rotate-90 text-purple-500"
                                : ""
                            }`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800 overflow-y-auto">
                {filtered.map((user) => (
                  <button
                    key={user._id}
                    onClick={() =>
                      setSelectedUser(
                        selectedUser?._id === user._id ? null : user
                      )
                    }
                    className={`w-full text-left px-4 py-4 flex items-center gap-3 transition ${
                      selectedUser?._id === user._id
                        ? "bg-purple-50 dark:bg-purple-900/10"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold flex-shrink-0">
                      {user.username?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.username}
                        </p>
                        <span
                          className={`ml-2 flex-shrink-0 w-2 h-2 rounded-full ${
                            user.isBlocked ? "bg-red-500" : "bg-green-500"
                          }`}
                        />
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        {user.email}
                      </p>
                      <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mt-0.5">
                        <Currency amount={user.wallet?.balance || 0} />
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: User detail panel */}
        {selectedUser && (
          <div className="hidden lg:flex flex-col w-2/5 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                User Details
              </h3>
              <button
                onClick={() => setSelectedUser(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Avatar + name */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-2xl font-bold">
                  {selectedUser.username?.[0]?.toUpperCase() || "U"}
                </div>
                <div>
                  <p className="text-base font-bold text-gray-900 dark:text-white">
                    {selectedUser.username}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedUser.email}
                  </p>
                  <span
                    className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedUser.isBlocked
                        ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                        : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        selectedUser.isBlocked ? "bg-red-500" : "bg-green-500"
                      }`}
                    />
                    {selectedUser.isBlocked ? "Blocked" : "Active"}
                  </span>
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    label: "Wallet Balance",
                    value: <Currency amount={selectedUser.wallet?.balance || 0} />,
                    icon: Wallet,
                    highlight: true,
                  },
                  {
                    label: "Total Spent",
                    value: <Currency amount={selectedUser.wallet?.totalSpent || 0} />,
                    icon: TrendingUp,
                  },
                  {
                    label: "Member Since",
                    value: new Date(selectedUser.createdAt).toLocaleDateString(
                      "en-NG",
                      { day: "numeric", month: "short", year: "numeric" }
                    ),
                    icon: Calendar,
                  },
                  {
                    label: "Role",
                    value: selectedUser.role || "user",
                    icon: Shield,
                  },
                ].map(({ label, value, icon: Icon, highlight }) => (
                  <div
                    key={label}
                    className={`p-3 rounded-xl border ${
                      highlight
                        ? "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10"
                        : "border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Icon
                        size={12}
                        className={
                          highlight
                            ? "text-purple-500"
                            : "text-gray-400"
                        }
                      />
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {label}
                      </span>
                    </div>
                    <p
                      className={`text-sm font-semibold capitalize ${
                        highlight
                          ? "text-purple-700 dark:text-purple-300"
                          : "text-gray-900 dark:text-white"
                      }`}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Wallet adjustment */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5">
                  <Wallet size={13} className="text-purple-500" />
                  Adjust Wallet Balance
                </p>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setAdjustType("add")}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg transition ${
                      adjustType === "add"
                        ? "bg-green-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <Plus size={12} />
                    Add
                  </button>
                  <button
                    onClick={() => setAdjustType("deduct")}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg transition ${
                      adjustType === "deduct"
                        ? "bg-red-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <Minus size={12} />
                    Deduct
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    placeholder="Amount (₦)"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                  />
                  <button
                    onClick={handleWalletAdjust}
                    disabled={adjusting || !adjustAmount}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                      adjustType === "add"
                        ? "bg-green-600 hover:bg-green-700 disabled:bg-green-400"
                        : "bg-red-600 hover:bg-red-700 disabled:bg-red-400"
                    } text-white flex items-center gap-1.5`}
                  >
                    {adjusting ? (
                      <RefreshCw size={13} className="animate-spin" />
                    ) : adjustType === "add" ? (
                      <Plus size={13} />
                    ) : (
                      <Minus size={13} />
                    )}
                    {adjusting ? "..." : "Apply"}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Account Actions
                </p>
                <button
                  onClick={() => handleBlock(selectedUser)}
                  disabled={actionLoading === `block-${selectedUser._id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition ${
                    selectedUser.isBlocked
                      ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 border border-green-200 dark:border-green-800"
                      : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800"
                  }`}
                >
                  {selectedUser.isBlocked ? (
                    <ShieldOff size={16} />
                  ) : (
                    <Shield size={16} />
                  )}
                  {actionLoading === `block-${selectedUser._id}`
                    ? "Processing..."
                    : selectedUser.isBlocked
                    ? "Unblock this user"
                    : "Block this user"}
                </button>

                <button
                  onClick={() => handleDelete(selectedUser)}
                  disabled={actionLoading === `delete-${selectedUser._id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-400 border border-gray-200 dark:border-gray-700 hover:border-red-200 dark:hover:border-red-800 transition"
                >
                  <Trash2 size={16} />
                  {actionLoading === `delete-${selectedUser._id}`
                    ? "Deleting..."
                    : "Delete account permanently"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile detail sheet — slides up from bottom */}
      {selectedUser && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedUser(null)}
          />
          <div className="relative w-full bg-white dark:bg-gray-900 rounded-t-2xl max-h-[80vh] overflow-y-auto">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>

            <div className="px-5 pb-8 pt-3 space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-lg font-bold">
                    {selectedUser.username?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {selectedUser.username}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {selectedUser.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X size={18} className="text-gray-500" />
                </button>
              </div>

              {/* Balance row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-xl p-3">
                  <p className="text-xs text-purple-500 mb-1">Balance</p>
                  <p className="text-sm font-bold text-purple-700 dark:text-purple-300">
                    <Currency amount={selectedUser.wallet?.balance || 0} />
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Total Spent</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    <Currency amount={selectedUser.wallet?.totalSpent || 0} />
                  </p>
                </div>
              </div>

              {/* Wallet adjust */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Adjust Wallet
                </p>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setAdjustType("add")}
                    className={`flex-1 py-2 text-xs font-medium rounded-lg ${
                      adjustType === "add"
                        ? "bg-green-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    + Add
                  </button>
                  <button
                    onClick={() => setAdjustType("deduct")}
                    className={`flex-1 py-2 text-xs font-medium rounded-lg ${
                      adjustType === "deduct"
                        ? "bg-red-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    − Deduct
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    placeholder="Amount (₦)"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                    className="flex-1 px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                  />
                  <button
                    onClick={handleWalletAdjust}
                    disabled={adjusting || !adjustAmount}
                    className={`px-4 py-2.5 text-sm font-medium rounded-lg text-white transition ${
                      adjustType === "add"
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    {adjusting ? "..." : "Apply"}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={() => handleBlock(selectedUser)}
                  disabled={actionLoading === `block-${selectedUser._id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition border ${
                    selectedUser.isBlocked
                      ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
                      : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800"
                  }`}
                >
                  {selectedUser.isBlocked ? (
                    <ShieldOff size={16} />
                  ) : (
                    <Shield size={16} />
                  )}
                  {selectedUser.isBlocked ? "Unblock this user" : "Block this user"}
                </button>
                <button
                  onClick={() => handleDelete(selectedUser)}
                  disabled={actionLoading === `delete-${selectedUser._id}`}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-red-600 border border-gray-200 dark:border-gray-700 transition"
                >
                  <Trash2 size={16} />
                  Delete account permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
